import type { AnalysisScope, DemoState, PlatformType, ReportRange } from "../types/demo";

export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(value);
};

export const formatShortDate = (iso: string): string => {
  return new Intl.DateTimeFormat("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(iso));
};

export const formatDateTime = (iso: string): string => {
  return new Intl.DateTimeFormat("en-AU", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
};

export const getRevenueTrend = (state: DemoState) => {
  return state.monthlyFinancials.map(item => ({
    label: item.month.slice(5),
    value: item.revenue,
  }));
};

export const getMarginLeakage = (state: DemoState) => {
  return [
    { label: "Materials", value: 98000 },
    { label: "Overtime", value: 74000 },
    { label: "Subcontract", value: 53000 },
    { label: "Travel", value: 21000 },
  ];
};

export const getKpis = (state: DemoState) => {
  const latest = state.monthlyFinancials[state.monthlyFinancials.length - 1] ?? state.monthlyFinancials[0]!;
  const previous = state.monthlyFinancials[state.monthlyFinancials.length - 2] ?? latest;
  const grossProfit = latest.revenue - latest.costOfSales;
  const margin = (grossProfit / latest.revenue) * 100;
  const prevMargin = ((previous.revenue - previous.costOfSales) / previous.revenue) * 100;
  const overdueInvoices = state.invoices.filter(invoice => invoice.status === "overdue");
  const overdueAmount = overdueInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  const projectsAtRisk = state.projects.filter(project => project.burnRisk === "high").length;

  return {
    revenueMtd: latest.revenue,
    grossProfit,
    margin,
    marginDelta: margin - prevMargin,
    cashIn7d: 286000,
    overdueCount: overdueInvoices.length,
    overdueAmount,
    projectsAtRisk,
  };
};

export const getPnLRows = (state: DemoState, range: ReportRange) => {
  const months =
    range === "this-month"
      ? state.monthlyFinancials.slice(-1)
      : range === "last-month"
        ? state.monthlyFinancials.slice(-2, -1)
        : range === "quarter"
          ? state.monthlyFinancials.slice(-3)
          : state.monthlyFinancials;

  const previousMonths =
    range === "this-month"
      ? state.monthlyFinancials.slice(-2, -1)
      : range === "last-month"
        ? state.monthlyFinancials.slice(-3, -2)
        : range === "quarter"
          ? state.monthlyFinancials.slice(-6, -3)
          : state.monthlyFinancials.slice(0, -months.length);

  const aggregate = (items: typeof months) => {
    return items.reduce(
      (totals, month) => {
        totals.revenue += month.revenue;
        totals.costOfSales += month.costOfSales;
        totals.operatingExpenses += month.operatingExpenses;
        return totals;
      },
      { revenue: 0, costOfSales: 0, operatingExpenses: 0 },
    );
  };

  const current = aggregate(months);
  const previous = aggregate(previousMonths.length > 0 ? previousMonths : months);
  const netProfit = current.revenue - current.costOfSales - current.operatingExpenses;
  const prevNet = previous.revenue - previous.costOfSales - previous.operatingExpenses;

  return [
    { label: "Revenue", value: current.revenue, variance: current.revenue - previous.revenue },
    { label: "Cost of Sales", value: current.costOfSales, variance: current.costOfSales - previous.costOfSales },
    {
      label: "Operating Expenses",
      value: current.operatingExpenses,
      variance: current.operatingExpenses - previous.operatingExpenses,
    },
    { label: "Net Profit", value: netProfit, variance: netProfit - prevNet },
  ];
};

export const getScopeConnections = (state: DemoState, scope: AnalysisScope) => {
  if (scope === "all") {
    return state.connections;
  }

  const group = state.groups.find(item => item.id === scope);
  if (group) {
    return state.connections.filter(connection => connection.groupIds.includes(group.id));
  }

  const connection = state.connections.find(item => item.id === scope);
  if (connection) {
    return [connection];
  }

  return [];
};

export const getScopePlatforms = (state: DemoState, scope: AnalysisScope): PlatformType[] => {
  const platforms = new Set(getScopeConnections(state, scope).map(connection => connection.platform));
  return [...platforms];
};

export const getScopeLabel = (state: DemoState, scope: AnalysisScope): string => {
  if (scope === "all") {
    return "All Connections";
  }

  const group = state.groups.find(item => item.id === scope);
  if (group) {
    return group.name;
  }

  const connection = state.connections.find(item => item.id === scope);
  if (connection) {
    return `${connection.name} (${connection.platform})`;
  }

  return "Unknown Scope";
};

export const getScopeType = (state: DemoState, scope: AnalysisScope): "all" | "group" | "connection" | "unknown" => {
  if (scope === "all") {
    return "all";
  }
  if (state.groups.some(group => group.id === scope)) {
    return "group";
  }
  if (state.connections.some(connection => connection.id === scope)) {
    return "connection";
  }
  return "unknown";
};
